import { useMemo } from "react"

import { Toast } from "../presentation/responses/toast"
import { describeRemoteError } from "../remote/remote-error-message"
import { useApplicationScope } from "./application-provider"

/** Shows short, non-blocking messages to the user as toasts. */
export interface Notify {
  success(message: string): void
  info(message: string): void
  warning(message: string): void
  /**
   * Shows a failed command, a remote error or any other thrown value as a localized message, see
   * `describeRemoteError`. A string is shown as is. A cancellation shows nothing.
   */
  error(cause: unknown): void
}

/**
 * Notifications through the presenter's `Toast` response of the current application scope, so a document's
 * toasts go with that document. Pass `error` straight to `onFailure` and `onError` of an application action.
 */
export function useNotify(): Notify {
  const { presenter, localization } = useApplicationScope()
  return useMemo(() => {
    const show = (severity: NonNullable<Toast["severity"]>, message: string) =>
      presenter.notify(Toast, { message, severity })
    return {
      success: (message) => show("success", message),
      info: (message) => show("info", message),
      warning: (message) => show("warning", message),
      error: (cause) => {
        const message = describeRemoteError(cause, localization.i18n.getFixedT(null, "florisoft-framework"))
        if (message != null) show("error", message)
      },
    }
  }, [presenter, localization])
}
